import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getPublicVisualizer, toAssetUrl } from '../../services/api';
import ProductPDF from './ProductPDF';

function adaptProduct(product) {
  const images = Array.isArray(product.images) && product.images.length > 0
    ? product.images.map(img => toAssetUrl(img.url))
    : [];

  const firstImage = images.length > 0 ? images[0] : null;

  return {
    id: product.id,
    name: product.name || product.sku || "Product",
    sku: product.sku || "1381",
    size: product.product_size || (product.size ? `${product.size.width}x${product.size.height} ${product.size.unit || 'mm'}` : "1200x1800mm"),
    image: firstImage,
    images: images.length > 0 ? images : [firstImage],
    product_family: product.product_family || product.category || "Evoluta",
    colour_group: product.colour_group || product.surface_type || "DG Matt",
    application_type: product.application_type || "Wall, Floor",
  };
}

export default function ProductDetail() {
  const { visualizerToken, productId } = useParams();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [retailer, setRetailer] = useState(null);
  const [selectedFace, setSelectedFace] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isFavorite, setIsFavorite] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchProduct = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await getPublicVisualizer(visualizerToken);
        if (cancelled) return;

        const payload = res?.data || res;
        setRetailer(payload?.retailer || null);
        
        const allProducts = [...(payload?.tiles || []), ...(payload?.wallpapers || [])];
        const foundProduct = allProducts.find(p => String(p.id) === String(productId));
        
        if (foundProduct) {
          setProduct(adaptProduct(foundProduct));
        } else {
          setError("Product not found");
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError("Failed to load product details");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (visualizerToken && productId) fetchProduct();
    return () => {
      cancelled = true;
    };
  }, [visualizerToken, productId]);

  const handleSeeInRoom = () => {
    if (visualizerToken) {
      navigate(`/visualizer/${visualizerToken}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center font-sans">
        <div className="text-gray-400 text-xs font-semibold">Loading product details…</div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6 font-sans">
        <div className="max-w-md text-center">
          <h1 className="text-lg font-bold text-gray-900 mb-2">
            Product Not Found
          </h1>
          <p className="text-gray-500 text-xs mb-4">{error}</p>
          <Link
            to={`/digital-showroom/${visualizerToken}`}
            className="text-xs font-semibold text-gray-700 hover:underline"
          >
            ← Back to Showroom
          </Link>
        </div>
      </div>
    );
  }

  const facesList = product.images && product.images.length > 0 
    ? product.images 
    : [product.image];

  return (
    <div className="min-h-screen bg-white flex flex-col font-sans text-gray-900">
      
      {/* Top Navigation Bar */}
      <header className="bg-white border-b border-gray-200 px-8 py-3.5 flex items-center justify-between sticky top-0 z-30">
        <div>
          {retailer?.logo_url ? (
            <img 
              src={toAssetUrl(retailer.logo_url)} 
              alt={retailer.company_name}
              className="h-7 max-w-36 object-contain"
            />
          ) : (
            <div>
              <h2 className="text-sm font-extrabold tracking-wider text-gray-900 uppercase">
                {retailer?.company_name || "YOUR COMPANY"}
              </h2>
              <span className="text-[9px] tracking-widest text-gray-400 font-bold block">
                LOGO HERE
              </span>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={handleSeeInRoom}
          className="px-4 py-2 bg-black hover:bg-gray-800 text-white text-xs font-semibold rounded-lg transition-colors inline-flex items-center gap-1.5"
        >
          Go to Visualizer ↗
        </button>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-6xl w-full mx-auto p-6 lg:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left Column: Product Visual Preview Box */}
        <div className="lg:col-span-6 space-y-3">
          <div className="relative border border-gray-200 rounded-xl bg-[#fbfbfb] p-8 min-h-[440px] flex flex-col items-center justify-between">
            
            {/* Top Right Heart Favorite Icon */}
            <button
              type="button"
              onClick={() => setIsFavorite(!isFavorite)}
              className="absolute top-4 right-4 w-7 h-7 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <svg 
                className={`w-3.5 h-3.5 ${isFavorite ? 'fill-red-500 text-red-500' : 'fill-none text-gray-400'}`} 
                viewBox="0 0 24 24" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </svg>
            </button>

            {/* Tile Image Display */}
            <div className="flex-1 flex items-center justify-center my-4 w-full">
              <img
                src={facesList[selectedFace] || '/placeholder-tile.webp'}
                alt={product.name}
                className="max-h-[300px] w-auto object-contain shadow-sm"
              />
            </div>

            {/* Inner See In My Room Action */}
            <button
              type="button"
              onClick={handleSeeInRoom}
              className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-xs font-semibold text-gray-800 rounded-md transition-colors shadow-sm inline-flex items-center gap-2"
            >
              <svg className="w-3.5 h-3.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 00-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 00-1 1m-6 0h6" />
              </svg>
              See this in my room
            </button>
          </div>

          {/* Random Faces Strip */}
          <div className="pt-1">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-2">
              RANDOM FACES ({facesList.length})
            </span>
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {facesList.map((imgUrl, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setSelectedFace(idx)}
                  className={`w-14 h-14 rounded-md border overflow-hidden flex-shrink-0 bg-stone-100 transition-all ${
                    selectedFace === idx 
                      ? 'border-gray-900 ring-1 ring-gray-900' 
                      : 'border-gray-200 hover:border-gray-400'
                  }`}
                >
                  <img 
                    src={imgUrl || '/placeholder-tile.webp'} 
                    alt={`Face ${idx + 1}`} 
                    className="w-full h-full object-cover" 
                  />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column: Specifications & Actions */}
        <div className="lg:col-span-6 space-y-6 pl-0 lg:pl-4">
          
          {/* Header Title + Share */}
          <div className="flex items-center justify-between">
            <h1 className="text-base font-bold text-gray-900 tracking-tight uppercase">
              {product.name}
            </h1>
            <button 
              type="button" 
              className="text-gray-600 hover:text-gray-900 p-1 transition-colors"
              title="Share"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            </button>
          </div>

          {/* Specifications Table */}
          <div>
            <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider mb-2">
              Specifications
            </h3>
            <div className="divide-y divide-gray-100 border-t border-b border-gray-100 text-xs font-sans">
              
              <div className="py-2.5 flex justify-between items-center">
                <span className="text-gray-500 font-medium">Size</span>
                <span className="font-semibold text-gray-800">{product.size}</span>
              </div>

              <div className="py-2.5 flex justify-between items-center">
                <span className="text-gray-500 font-medium">SKU</span>
                <span className="font-semibold text-gray-800">{product.sku}</span>
              </div>

              <div className="py-2.5 flex justify-between items-center">
                <span className="text-gray-500 font-medium">Application Type</span>
                <span className="font-semibold text-gray-800 capitalize">{product.application_type}</span>
              </div>

              <div className="py-2.5 flex justify-between items-center">
                <span className="text-gray-500 font-medium">PRODUCT FAMILY</span>
                <span className="font-semibold text-gray-800">{product.product_family}</span>
              </div>

              <div className="py-2.5 flex justify-between items-center">
                <span className="text-gray-500 font-medium">COLOUR GROUP</span>
                <span className="font-semibold text-gray-800">{product.colour_group}</span>
              </div>

            </div>
          </div>

          {/* Action Row Buttons */}
          <div className="grid grid-cols-3 gap-2.5 pt-1">
            <ProductPDF product={product} />

            <button
              type="button"
              className="py-2 px-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-800 rounded-md text-[11px] font-semibold transition-colors inline-flex items-center justify-center gap-1.5"
            >
              <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              Send Inquiry
            </button>

            <button
              type="button"
              className="py-2 px-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-800 rounded-md text-[11px] font-semibold transition-colors inline-flex items-center justify-center gap-1.5"
            >
              <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              Virtual Sample
            </button>
          </div>

        </div>

      </main>
    </div>
  );
}